// Portal i18n dictionary — English + Bahasa Melayu.
//
// Locked by docs/revamp/05_PORTAL_DESIGN.md §4: both portals ship EN + MS with a
// runtime switcher; strings live here, never hard-coded. Layouts are RTL-ready
// (logical start/end, `dir`) so Arabic can follow — `LOCALE_DIR` carries the
// per-locale direction for that future locale without changing call sites now.
//
// The copy mirrors the translated `L('…', '…')` pairs in the HTML prototypes
// (docs/design/zooyloresponsive/project/{Service,Admin} Portal.dc.html). Only
// complete, standalone strings are seeded — sentence fragments meant for runtime
// concatenation (names, dates, sample data) are intentionally left out so the
// dictionary stays clean and sentence-case-disciplined.
//
// Guardrail discipline (docs/CLAIMS_POLICY.md): companion, not therapy; the copy
// here says "engagement, not scores" and never implies diagnosis or assessment.

export const LOCALES = ["en", "ms"] as const;
export type Locale = (typeof LOCALES)[number];

export const DEFAULT_LOCALE: Locale = "en";

/** Human-facing name of each locale, shown in the language switcher. */
export const LOCALE_LABEL: Record<Locale, string> = {
  en: "English",
  ms: "Bahasa Melayu",
};

/** Writing direction per locale. RTL-ready for when Arabic is added. */
export const LOCALE_DIR: Record<Locale, "ltr" | "rtl"> = {
  en: "ltr",
  ms: "ltr",
};

// The English dictionary is the canonical shape; `PortalDictionary` is derived
// from it so every other locale is checked for parity at compile time, and the
// companion test re-checks it at runtime (keys, empties, sentence case).
const en = {
  common: {
    signOut: "Sign out",
    cancel: "Cancel",
    continue: "Continue",
    saveChanges: "Save changes",
    sendMagicLink: "Send my magic link",
    sending: "Sending…",
    checkInbox: "Check your inbox",
    linkExpired: "That link has expired",
    sendNewLink: "Send a new link",
    useDifferentEmail: "Use a different email",
    orUseCode: "Can't open the link? Enter the code from the same email.",
    codeLabel: "Code from the email",
    signInWithCode: "Sign in with code",
    checkingCode: "Checking…",
    codeInvalid: "That code didn't work. It may have expired: send a new link and use the newest email.",
    codeFormat: "Enter the 6 to 10 digit code from the email.",
  },

  comfort: {
    title: "Comfort & display",
    calm: "Calm",
    text: "Text",
    spacing: "Spacing",
    contrast: "Contrast",
    dark: "Dark",
    light: "Light",
    calmMode: "Calm mode",
    biggerText: "Bigger text",
    readingSpacing: "Reading spacing",
    higherContrast: "Higher contrast",
    darkTheme: "Dark theme",
  },

  language: {
    title: "Language",
    subtitle:
      "English and Bahasa Melayu now — more, including right-to-left languages, are on the way.",
  },

  service: {
    nav: {
      space: "Your space",
      home: "Home",
      children: "Children",
      assign: "Assign practice",
      export: "Progress export",
      link: "Link a child",
      consent: "Sharing & consent",
      settings: "Settings",
    },
    signIn: {
      title: "Sign in to Zooylo",
      body: "No passwords here. Pop in your email and we'll send a magic link — one tap and you're in.",
      emailLabel: "Your email",
      emailHint: "We'll only email about Zooylo. No spam, ever.",
      guardrail: "A skill-building companion — not a therapy or diagnostic tool.",
      expiredBody:
        "Magic links last 15 minutes to keep your account safe. No worries — we can send a fresh one right now.",
    },
    home: {
      greeting: "Good morning",
      thisWeek: "This week",
      sessionsPlayed: "Sessions played",
      childrenActive: "Children active",
      summariesShared: "Summaries shared",
      engagementOnly: "Engagement only — never scores or outcomes.",
      seeProgress: "See full progress",
      lastPlayed: "Last played {date}",
      rosterTitle: "This week, child by child",
      rosterDays: "Days played: {n}",
      rosterSteps: "Steps that moved up: {n}",
      rosterLongHelp: "With a little help for 6+ weeks: {n}",
      rosterSeen: "Suggestions seen: {n}",
      rosterQuiet: "No play synced this week yet",
      noStepsYet: "No skill steps shared yet. Recent play shows on the child's page.",
    },
    children: {
      title: "Your children",
      ownerTag: "You own this",
      viewerTag: "Shared by",
      pendingTag: "Awaiting consent",
      revokedTag: "Access removed",
      emptyTitle: "No children linked yet",
      emptyBody:
        "Link a child with a code, or share your own code so another adult can join. Nothing is shared without consent.",
      loading: "Loading your children…",
    },
    childDetail: {
      yourRole: "Your role: {role}",
      yourLabel: "How you appear to the family",
      labelHint: "A short name for you, like Ms Aisyah, speech. About you, never the child.",
      saveLabel: "Save",
      labelSaved: "Saved.",
      labelError: "We couldn't save that. Please try again.",
      editLabel: "Edit",
      noLabel: "No label yet",
      adultsTitle: "Adults supporting this child",
      adultsNone: "You haven't shared with anyone yet.",
      adultActive: "Sharing on",
      adultPending: "Waiting for you to share",
      adultRevoked: "Sharing ended",
      adultsError: "We couldn't load who you share with.",
      roleFamily: "Family",
      roleTherapist: "Therapist",
      roleTeacher: "Teacher",
      roleCaregiver: "Caregiver",
      roleOther: "Other",
      backToChildren: "Back to children",
      tabsLabel: "About this child",
      tabOverview: "Overview",
      tabMessages: "Messages",
      messagesTitle: "Practice ideas for this child",
      messagesIntro: "Every idea sent for this child, by you and by the people helping, newest first.",
      messagesIntroHelper: "The ideas you sent for this child, newest first. Other helpers' ideas stay between them and the family.",
      messagesNone: "No practice ideas have been sent for this child yet.",
      messagesHidden: "Practice ideas show here when the family shares assigned-practice results with you.",
      messagesLoading: "Loading practice ideas…",
      messagesError: "We couldn't load the practice ideas.",
      messagesRetry: "Try again",
      messagesSend: "Send practice ideas",
      senderYou: "You",
      senderOther: "Another helper",
      sentOn: "Sent {date}",
      sentGames: "Games in this message",
      recentPlay: "Recent play",
      noSessions: "No recent play sessions",
      loading: "Loading…",
      notLinked: "This child is not linked to your account.",
      noStepsYet:
        "Skill steps appear here once the family's app shares them. An older version of the app shares recent play only, which is below.",
      footer: "What we saw during play at home. Not an assessment, screening or diagnosis.",
      pendingTitle: "Awaiting consent",
      pendingBody:
        "The owner hasn't confirmed sharing yet. You'll see engagement summaries here once they do.",
      revokedTitle: "Access removed",
      revokedBody:
        "The owner has removed your access to this child's engagement summaries.",
    },
    assign: {
      title: "Assign practice",
      subtitle:
        "Choose guides and games that map to the skills you're gently targeting. Children just open the app and play.",
      step1: "Pick a child",
      step2: "Choose up to 3 games",
      step3: "Add a note and send",
      startButton: "Start an assignment",
      chosenCount: "{n} of {max} chosen",
      maxReached: "That's 3, a short set a family can follow at home. Untick one to choose a different game.",
      next: "Next",
      back: "Back",
      confirmFor: "For",
      confirmGames: "Games",
      alreadySent: "Sent before: {games}. Sending again adds a fresh idea.",
      confirmBody: "The family sees these as practice ideas in their app. Children just play; nothing here is a task.",
      sendButton: "Send practice ideas",
      sending: "Sending…",
      sentTitle: "Practice ideas sent",
      sentBody: "Sent for {child}: {games}. The family sees them next time they open the app.",
      sendMore: "Send more ideas",
      viewChild: "View {child}",
      chooseOne: "Choose at least one game.",
      tooMany: "Choose up to 3 games at a time.",
      noteTooLong: "The note can be up to 280 characters.",
      noteLabel: "A note for the family (optional)",
      noteHint: "One note for all the games you chose. The family sees it in their app.",
      notePrivacy: "Please don't include names or health details.",
      noteCount: "Characters: {n} of {max}",
      needResults:
        "The family hasn't shared \"Assigned-practice results\" with you yet, so a suggestion can't reach their app. They can turn it on in Sharing & consent.",
      sendError: "Couldn't send that just now. Please try again.",
      premiumTag: "Premium",
      premiumLocked: "This family is on the free version, so Premium games can't be suggested.",
    },
    export: {
      title: "Progress export",
      subtitle:
        "Generate a calm, claims-clean summary — meeting and review ready. No scores, no jargon, no diagnoses.",
      bullet1: "Plain language, framed around strengths",
      bullet2: "Engagement only — no diagnostic claims",
      bullet3: "Carries the guardrail line automatically",
      generateButton: "Generate PDF",
      shareButton: "Share",
      preparing: "Preparing…",
      shareTitle: "Zooylo support one-pager",
      shareText: "What we saw during play at home. Not an assessment, screening or diagnosis.",
    },
    link: {
      title: "Link a child",
      subtitle:
        "A child is created on their own device and stays a pseudonymous code. Link with a code — either one shared with you, or one you share to invite another adult.",
      haveTab: "Someone shared a code with me",
      shareTab: "I look after this child",
      codeLabel: "Link code",
      linkButton: "Link this child",
    },
    consent: {
      title: "Sharing & consent",
      scopeProgress: "Progress summaries",
      scopeResults: "Assigned-practice results",
      scopeExport: "Allow progress export",
      revokeAll: "Revoke all access",
      immediateNote:
        "Revoking cuts access immediately and purges the shared copies held by that adult. You keep your own data on this device.",
      whatShared: "What's shared",
      neverShared: "What's never shared",
    },
    settings: {
      title: "Settings",
      subtitle: "Your account, language, comfort modes and how Zooylo feels for you.",
      accountTitle: "Account",
      signedInAs: "Signed in as",
      signInMethod: "Sign-in method",
      magicLink: "Magic link · no password",
    },
    sync: {
      lastSynced: "Last synced",
      note: "Consented engagement summaries — they may lag a little, as children play offline.",
    },
    progress: {
      seenAtHomeTitle: "Seen at home",
      seenAtHomeNone: "No notes shared from home in this period.",
      seenAtHomeWeek: "Week of {date}",
      seenAtHomeNote: "Written by the family in the app and shared with you.",
      onePagerTitle: "Support one-pager",
      sharedWithFamily: "Shared with the family",
      period: "Last 13 weeks: {from} to {to}",
      supportTitle: "How best to support me",
      supportNone: "No settings shared from the app yet.",
      supportDefaults: "Uses the app's standard settings.",
      captions: "Captions on",
      reducedMotion: "Reduced motion",
      highContrast: "High contrast",
      calmMode: "Calm mode",
      tapOnly: "Tap instead of drag",
      language: "Plays in {language}",
      langEn: "English",
      langMs: "Bahasa Melayu",
      practiceTitle: "Practice at home this period",
      daysPlayed: "Played on {n} days",
      daysPlayedOne: "Played on 1 day",
      daysPlayedNone: "No play synced in this period",
      activeWeeks: "Active in {n} of {total} weeks",
      stepsMovedUp: "Steps that moved up a stage: {n}",
      stepsInProgress: "Steps being worked on: {n}",
      newTitle: "New since the last review",
      newNone: "Nothing new this period, and that's fine.",
      workingTitle: "Working on now",
      workingNone: "No steps at this stage right now.",
      stepLine: "{skill}, step {n}",
      reachedOn: "Reached {date}",
      since: "With a little help since {date}",
      lastPractised: "Last played {date}",
      notPractised: "Not played yet",
      areasTitle: "Steps with no game help, start and end of this period",
      areasNote: "Each area is compared only with this child's own start of the period.",
      areaAria: "{area}: {start} at the start of the period, {end} at the end",
      timelineTitle: "Timeline",
      timelineHint:
        "Pale band: steps started. Dark band: steps with no game help. Dotted lines: review points every 13 weeks.",
      summaryTitle: "Steps with no game help, by stage",
      summaryAria: "Week of {date}: {own} with no game help, {steady} steady, {places} in new places",
      rowAria: "{skill}, week of {date}: {started} of 4 steps started, {own} with no game help",
      rowCounts: "Started {started} of 4 · no game help {own}",
      rangeLabel: "Time range",
      range3m: "3 months",
      range6m: "6 months",
      range1y: "1 year",
      rangeAll: "All",
      reviewMarker: "Review point, week of {date}",
      weekOf: "Week of {date}",
      stepStage: "Step {n}: {stage}",
      statusAssigned: "Not opened yet",
      statusOpened: "Opened by the family",
      statusSeen: "Seen by the family on {date}",
      statusPlayed: "Played",
      stageNone: "Not started",
      stageExploring: "Exploring",
      stageHelp: "With a little help",
      stageOwn: "No game help",
      stageSteady: "Steady",
      stagePlaces: "In new places",
      bedLanguage: "Words & listening",
      bedLiteracy: "Sounds & letters",
      bedNumeracy: "Numbers",
      bedExecutiveFunction: "Memory, patterns & sorting",
      bedSocialEmotional: "Feelings & getting along",
      bedDailyLife: "Everyday life & safety",
      bedMotor: "Hands & tracing",
      bedPerception: "Looking & colours",
      bedCreativity: "Making & imagining",
      bedAttention: "Attention",
      bedOther: "Other",
    },
  },

  console: {
    title: "Zooylo console",
    subtitle: "Organisations, licence terms, invoices and access codes.",
    eyebrow: "Owner console",
    notFoundTitle: "Page not found",
    notFoundBody: "There is nothing at this address.",
    backHome: "Go to the portal",
    tabOrgs: "Organisations",
    tabCodes: "Access codes",
    colName: "Organisation",
    colKind: "Kind",
    colStatus: "Status",
    colAdmins: "Admins",
    colMembers: "Members",
    colLicences: "Licences",
    colPrice: "Price",
    colOutstanding: "Outstanding",
    kindOrg: "Organisation",
    kindIndividual: "Individual practitioner",
    stActive: "Active",
    stSuspended: "Suspended",
    stClosed: "Closed",
    licencesOf: "{live} of {allowance}",
    noOrgs: "No organisations yet.",
    manage: "Manage",
    closePanel: "Close",
    termsTitle: "Licence terms",
    seats: "Staff seats",
    allowance: "Family licences",
    price: "Zooylo's share (per month)",
    retailPrice: "Family pays the organisation (per month)",
    orgKeeps: "Organisation keeps",
    orgKeepsHint: "What the family pays, less Zooylo's share. Worked out for you.",
    sstRate: "SST rate (%)",
    errShareAboveRetail: "Zooylo's share cannot be more than what the family pays the organisation.",
    errSstRange: "SST must be between 0 and 100 per cent.",
    currency: "Currency",
    billingEmail: "Billing email",
    contractEnd: "Contract ends",
    saveTerms: "Save terms",
    saved: "Saved.",
    statusTitle: "Status",
    suspend: "Suspend",
    reactivate: "Reactivate",
    closeOrg: "Close organisation",
    endLicencesLabel: "Also end all family licences now",
    endLicencesWarn: "Every family on this organisation's licences goes back to the free animals at their next app refresh. Ended licences cannot be restored from here.",
    statusWarnSuspend: "Suspending stops new family licences. Admins can still sign in and see their screens.",
    statusWarnClose: "Closing marks the organisation as closed. Admins see a closed notice.",
    statusWarnReactivate: "Reactivating lets this organisation give family licences again.",
    confirmStatus: "Confirm",
    statusSaved: "Status updated.",
    invoiceTitle: "Issue an invoice",
    periodStart: "Period starts",
    periodEnd: "Period ends",
    amountLabel: "Amount, optional",
    amountHint: "Leave empty to charge licence-months times Zooylo's share, worked out when the invoice is issued. An amount entered replaces the subtotal; SST is always added on top.",
    lastMonthEstimate: "Last month came to {amount}.",
    periodEstimate: "For this period: {lm} licence-months. Subtotal {subtotal}, SST ({rate}%) {sst}, total {total}.",
    due: "Due date",
    note: "Note",
    issue: "Issue invoice",
    issued: "Invoice issued.",
    invoicesTitle: "Invoices",
    noInvoices: "No invoices for this organisation.",
    markPaid: "Mark paid",
    markVoid: "Void",
    markIssued: "Mark issued",
    codesTitle: "Access codes",
    newCode: "Create an access code",
    codeLabel: "Label",
    codeKind: "For",
    codeSeats: "Staff seats",
    codeAllowance: "Family licences",
    codePrice: "Zooylo's share (per month)",
    codeRetailPrice: "Family pays the organisation (per month)",
    codeUses: "Uses",
    codeExpires: "Expires on",
    create: "Create code",
    creating: "Creating…",
    newCodeTitle: "New access code",
    newCodeBody: "Give this code to the organisation. It is shown here once, so copy it now.",
    copy: "Copy",
    copied: "Copied",
    colCode: "Code",
    colLabel: "Label",
    colTerms: "Terms",
    colUses: "Uses",
    colExpires: "Expires",
    termsLine: "{seats} seats · {families} licences · {price}",
    never: "Never",
    noCodes: "No access codes yet.",
    loading: "Loading…",
    errorTitle: "We couldn't load the console",
    retry: "Try again",
    errNotAuth: "Only Zooylo platform admins can do that.",
    errGeneric: "That didn't go through. Nothing has changed. Please try again.",
    errInvalid: "Check the values and try again.",
    demoNote: "Demo data. Nothing here reaches a real organisation.",
  },

  admin: {
    nav2: {
      children: "Children",
      licences: "Family licences",
      billing: "Billing",
      users: "Users",
      settings: "Settings",
      consentData: "Consent & data",
      consent: "Consent & privacy",
      data: "Data rights",
      console: "Zooylo console",
      demoAs: "Demo organisation",
      demoCentre: "Centre",
      demoIndividual: "Individual practitioner",
      demoSuspended: "Suspended centre",
    },
    status: {
      suspendedTitle: "Your organisation is paused",
      suspendedBody: "Zooylo has paused this account. Families keep what they have and you can still see every screen, but new family licences cannot be given. Write to support@zooylo.app to talk it through.",
      closedTitle: "This organisation is closed",
      closedBody: "This account has been closed. You can still see its records. Write to support@zooylo.app if you have a question about it.",
    },
    licences: {
      title: "Family licences",
      subtitle: "A family licence gives one family full app access, paid by your organisation. You need the parent or guardian's email address. Nothing about a child appears here.",
      chargeNote: "A licence is charged from the day the family signs in, pro-rated by day.",
      allowance: "In your plan",
      given: "Given",
      claimed: "Signed in",
      pending: "Waiting to sign in",
      remaining: "Left to give",
      noAllowanceTitle: "Your plan has no family licences",
      noAllowanceBody: "You can still link to children and send practice suggestions. Families who want full access can subscribe in the app, or ask us about adding licences.",
      formTitle: "Give a licence",
      emailLabel: "Parent or guardian's email",
      endsLabel: "Licence ends on",
      checkButton: "Check this email",
      checking: "Checking…",
      giveButton: "Give licence",
      giving: "Giving…",
      checkPaid: "Already has Premium through the app store. No licence needed.",
      checkLicensed: "Already licensed by you.",
      checkAvailable: "Ready to give. You have {n} left.",
      checkFull: "No licences left. End one you no longer need, or ask us to add more.",
      checkNoAllowance: "Your plan has no family licences.",
      checkSuspended: "Your organisation is suspended, so licences cannot be given right now.",
      emailInvalid: "Enter an email address, like name@example.com.",
      dateInvalid: "Choose an end date in the future.",
      givenOk: "Licence given to {email}. We email the family how to start: they sign in to the app with this address.",
      tableTitle: "Licences",
      colFamily: "Family",
      colStatus: "Status",
      colEnds: "Ends",
      colGiven: "Given",
      colActions: "Actions",
      statusPending: "Waiting to sign in",
      statusActive: "Signed in",
      statusLapsed: "Ended",
      statusRevoked: "Ended early",
      endingSoon: "Ends in {n} days",
      endingToday: "Ends today",
      changeEnd: "Change end date",
      newEndLabel: "New end date for {email}",
      saveDate: "Save date",
      endNow: "End now",
      endConfirmTitle: "End this licence now?",
      endConfirmBody: "The family goes back to the free animals the next time their app connects. Their child's play stays on their device.",
      endConfirmButton: "End licence",
      endedOk: "Licence ended.",
      dateSaved: "End date saved.",
      empty: "No licences given yet.",
      showPast: "Show ended licences ({n})",
      hidePast: "Hide ended licences",
      demoPaidHint: "Demo: siti.rahman@example.com already pays in the app store.",
      loading: "Loading licences…",
      errorTitle: "We couldn't load your licences",
      retry: "Try again",
      errGeneric: "That didn't go through. Nothing has changed. Please try again.",
      errNotAuth: "Only an admin of this organisation can do that.",
      errRefused: "That licence could not be given. Check the email and end date, then try again.",
      bulkTitle: "Give many licences at once",
      bulkIntro: "Paste parent or guardian emails, one per line or separated by commas, or choose a CSV file. Every licence in this batch ends on the same date.",
      bulkPasteLabel: "Parent or guardian emails",
      bulkFileLabel: "Or choose a CSV file",
      bulkFileHint: "We use the column named email, or the first column. A header row is skipped.",
      bulkFileLoaded: "Read from {file}.",
      bulkFileError: "That file couldn't be read. Save it as a CSV file and try again.",
      bulkEndsLabel: "Licences end on",
      bulkReady: "{n} emails ready.",
      bulkInvalidNote: "{n} are not emails. They will be listed as not an email.",
      bulkDuplicateNote: "{n} repeated emails are counted once.",
      bulkLeftNote: "You have {n} licences left. Emails past that will be listed as no licences left.",
      bulkOverCap: "That is more than 2000 emails. Split the list and upload it in parts.",
      bulkGive: "Give {n} licences",
      bulkGiving: "Giving licences…",
      bulkResultsTitle: "Results",
      bulkSummary: "Licences given: {given}. Not given: {other}.",
      bulkColEmail: "Email",
      bulkColResult: "Result",
      bulkStLicensed: "Licensed",
      bulkStAlready: "Already licensed",
      bulkStPaid: "Pays in the app store, no licence needed",
      bulkStInvalid: "Not an email",
      bulkStFull: "No licences left",
      bulkStSuspended: "Organisation suspended",
      bulkNewList: "Start a new list",
      bulkDemoHint: "Demo: siti.rahman@example.com pays in the app store, and priya.menon@example.com is already licensed.",
      selectRow: "Select {email}",
      selectAllShown: "Select all shown",
      selectBeforeLabel: "Select all ending before",
      selectBeforeButton: "Select",
      selectedCount: "{n} selected",
      clearSelection: "Clear selection",
      bulkEndLabel: "New end date for the selected licences",
      bulkEndButton: "Change {n} end dates",
      bulkEndDone: "End date changed for {n} licences.",
      bulkEndAllowance: "That would bring back more licences than your plan has room for. Choose fewer, or end some you no longer need first.",
      fixEmail: "Fix email",
      fixTitle: "Fix the email on this licence",
      fixLabel: "The right email",
      fixBodyPending: "No one has signed in with {email} yet, so the licence simply moves to the right email.",
      fixBodyClaimed: "Someone already signed in with {email}. Their access ends now, and the right email gets a new licence with the same end date.",
      fixButton: "Save email",
      fixDone: "Email fixed. The licence is now for {email}.",
    },
    billing: {
      title: "Billing",
      subtitle: "What your family licences cost, and the invoices we have sent you. Staff seats are free.",
      chargeNote: "A licence is charged from the day the family signs in, pro-rated by day.",
      price: "Zooylo's share per family licence",
      perMonth: "{amount} a month",
      plusSst: "Plus {rate}% SST",
      noSst: "No SST added",
      arrangementTitle: "How family licences are paid",
      arrangement: "Families pay you {retail} a month; you keep {keep}; Zooylo invoices you {share} per family per month, plus {rate}% SST.",
      arrangementNoSst: "Families pay you {retail} a month; you keep {keep}; Zooylo invoices you {share} per family per month, with no SST.",
      arrangementShareOnly: "Zooylo invoices you {share} per family per month, plus {rate}% SST.",
      arrangementShareOnlyNoSst: "Zooylo invoices you {share} per family per month, with no SST.",
      arrangementCollect: "You collect from families yourselves. Zooylo sends you one invoice a month.",
      amountsIncludeSst: "The amounts below include SST.",
      live: "Licences live",
      claimedOf: "{claimed} signed in, {pending} waiting",
      monthToDate: "This month so far",
      lmLine: "{n} licence-months",
      projected: "This month if nothing changes",
      projectedHint: "Every signed-in licence for the whole month",
      lastMonth: "Last month",
      outstanding: "Outstanding",
      outstandingNone: "Nothing outstanding",
      invoicesTitle: "Invoices",
      colNumber: "Invoice",
      colPeriod: "Period",
      colLm: "Licence-months",
      colAmount: "Amount",
      colSubtotal: "Subtotal",
      colSst: "SST amount",
      colTotal: "Total",
      colStatus: "Status",
      colDue: "Due",
      stIssued: "Issued",
      stPaid: "Paid",
      stVoid: "Void",
      stDraft: "Draft",
      noInvoices: "No invoices yet.",
      contract: "Contract",
      contractRange: "{from} to {to}",
      contractFrom: "Since {from}",
      freePlanTitle: "Nothing to pay",
      freePlanBody: "Your plan has no family licences, so there is nothing to charge. Seats for your team are always free.",
      payNote: "Pay by bank transfer with the invoice number as the reference. Questions: support@zooylo.app",
      loading: "Loading billing…",
      errorTitle: "We couldn't load billing",
      retry: "Try again",
    },
    analytics: {
      title: "At a glance",
      subtitle: "Counts only. Never a child's progress.",
      people: "People",
      membersLine: "{members} members, {admins} admins",
      justYou: "Just you",
      children: "Children",
      linked: "Linked",
      active30: "Played in the last 30 days",
      inactive: "Moved to inactive",
      licences: "Family licences",
      live: "Live",
      claimed: "Signed in",
      endingSoon: "Ending in 30 days",
      suggestions: "Practice suggestions, last 30 days",
      sent: "Sent",
      seen: "Seen by the family",
      played: "Played",
      chartTitle: "Licence-months by month",
      chartAria: "{month}: {n} licence-months",
      chartNote: "The current month shows the days so far.",
      loading: "Loading counts…",
      errorTitle: "We couldn't load these counts",
      retry: "Try again",
    },
    kids: {
      title: "Children",
      subtitle: "The children your team supports, shown as a code, with whether they have been playing. Never their progress.",
      refNote: "Each child shows as a coded reference, never a name. The code is unique to your organisation and cannot be traced back. A child's identity stays with their family.",
      tabActive: "Active ({n})",
      tabInactive: "Inactive ({n})",
      colChild: "Child",
      colSupported: "Supported by",
      colSharing: "Sharing",
      colActivity: "Activity",
      colLicence: "Licence",
      colActions: "Actions",
      childCode: "Child · {ref}",
      actActive: "Active",
      actInactive: "Inactive",
      actUnknown: "Not synced",
      actNote: "Active means the family's app synced play in the last 30 days.",
      licLicensed: "Licensed by you",
      licPaid: "Pays in app",
      licFree: "Free",
      shareActive: "Sharing on",
      sharePending: "Waiting for the family",
      shareRevoked: "Sharing ended",
      moveInactive: "Move to inactive",
      moveActive: "Move back to active",
      endSupport: "End support",
      endTitle: "End support for this child?",
      endBody: "Every link your team holds to this child is removed, with the suggestions you sent. The family keeps everything in their app and can link with you again using a new code.",
      endButton: "End support",
      endedOk: "Support ended. Links removed: {n}.",
      movedInactive: "Moved to inactive.",
      movedActive: "Moved back to active.",
      inactiveNote: "Moving a child to inactive changes nothing for the family and removes no link.",
      emptyActive: "No children linked yet",
      emptyActiveBody: "A child appears here once a family shares with someone in your team.",
      emptyInactive: "No inactive children",
      emptyInactiveBody: "Move a child here when they leave, so your active list stays current. Nothing changes for the family.",
      loading: "Loading children…",
      errorTitle: "We couldn't load the children",
      retry: "Try again",
      errGeneric: "That didn't go through. Nothing has changed. Please try again.",
      roleFamily: "Family",
      roleTherapist: "Therapist",
      roleTeacher: "Teacher",
      roleCaregiver: "Caregiver",
      roleOther: "Other",
      colConsent: "Family",
      consentShared: "Family shared",
      consentNot: "Not shared yet",
      notSharedNote: "The family can share this child with your organisation from the app after signing in.",
      assignStaff: "Assign staff ({n})",
      closeStaff: "Close",
      staffTitle: "Staff for {child}",
      staffIntro: "Assigned staff see this child in their caregiver portal for as long as the family keeps sharing with your organisation.",
      staffNone: "No staff assigned yet.",
      staffRemove: "Remove",
      staffRemoveLabel: "Remove {email}",
      staffRemoved: "Removed. They no longer see this child.",
      staffAdd: "Add a staff member",
      staffMember: "Staff member",
      staffChoose: "Choose a person",
      staffRole: "Role",
      staffAssign: "Assign",
      staffAssigned: "{email} is assigned.",
      staffNoMembers: "Everyone in your organisation is already assigned to this child.",
      staffLoading: "Loading staff…",
      staffErrNotShared: "The family has not shared this child with your organisation yet.",
      staffErrDirect: "This person already has their own link to this child from the family.",
      staffErrNotMember: "Only members of your organisation can be assigned.",
    },
    users: {
      title: "Users",
      seatsFree: "Seats for your team are free.",
      justYouTitle: "Just you for now",
      justYouBody: "You run this account on your own. If a colleague joins you later, invite them here.",
      inviteColleague: "Invite a colleague",
      soloSubtitle: "Your account. Invite a colleague later if you start working together.",
    },
    nav: {
      manage: "Manage",
      overview: "Overview",
      members: "Members",
      seats: "Seats & licence",
      sponsored: "Sponsored families",
      oversight: "Oversight",
      roster: "Roster oversight",
      consent: "Consent & privacy",
      data: "Data rights",
      settings: "Org settings",
    },
    signIn: {
      title: "Sign in to manage your organisation",
      body: "No passwords. Enter your work email and we'll send a magic link to sign you in.",
      emailLabel: "Work email",
      emailHint: "Only people invited to your organisation can sign in here.",
      guardrail: "Children stay pseudonymous — admins never see a child's progress.",
      notMemberTitle: "This email isn't part of an organisation yet",
    },
    overview: {
      title: "Organisation overview",
      subtitle: "A snapshot of your licence health and recent activity.",
      licenceHealth: "Licence health",
      planActive: "Plan active",
      seatsFreeLabel: "Seats free",
      needMoreSeats: "Need more seats?",
      membersWord: "Members",
      linkedChildrenLabel: "Children linked",
      pseudonymousNote: "Pseudonymous aggregate — admins never see a child's progress",
      pendingInviteLabel: "Pending invite",
      pendingWhoLabel: "Awaiting acceptance",
      recentActivity: "Recent activity",
    },
    members: {
      title: "Members",
      subtitle:
        "Invite the people who support children — by email. They sign in with a magic link; no passwords to manage.",
      inviteButton: "Invite member",
      colMember: "Member",
      colRole: "Role",
      colSeat: "Seat",
      colStatus: "Status",
    },
    seats: {
      title: "Seats & licence",
      subtitle:
        "Assign, reassign or revoke a seat for any member. Your plan is negotiated with us — there's no checkout here.",
      capacity: "Seat capacity",
      contactUs: "Contact us",
    },
    sponsored: {
      title: "Sponsored families",
      subtitle:
        "Give a family you support full access to the app on your organisation's licence, until a date you choose. You need the parent or guardian's email address — nothing about a child appears here.",
      allowanceLabel: "Sponsorships",
      contactUs: "Contact us",
    },
    roster: {
      title: "Roster oversight",
      subtitle:
        "Every child linked across your organisation — who supports them and their sharing status. Aggregate only; admins never see a child's progress.",
      emptyTitle: "No children linked yet",
    },
    consent: {
      title: "Consent & privacy oversight",
      subtitle:
        "A clear log of who can access which child, and the consent behind it. Consent is granted and withdrawn by the child's parent or guardian — your organisation can audit it, never change it.",
      banner:
        "Each child is a pseudonymous UUID with no personal data stored on our servers. Access always traces back to an explicit, revocable parent consent. Data is stored under your region's data-protection law.",
    },
    data: {
      title: "Data-subject rights",
      subtitle:
        "Export what your organisation holds, or remove it. Children stay pseudonymous throughout — there's no child personal data to export.",
      exportButton: "Export organisation data",
      closeTitle: "Close the organisation account",
    },
    settings: {
      title: "Org settings",
      subtitle: "Your organisation profile, language and how the portal feels for you.",
      residencyTitle: "Data residency region",
      residencyBody:
        "Set where your organisation's records are stored, per your local data-protection law. No region is assumed by default.",
    },
  },
} as const;

/**
 * The shape every locale must satisfy — derived from the canonical EN copy.
 * Recurses the namespaces, widening each leaf to `string` so a locale must
 * provide the exact same keys (enforced at compile time, re-checked at runtime).
 */
type Translated<T> = {
  readonly [K in keyof T]: T[K] extends string ? string : Translated<T[K]>;
};
export type PortalDictionary = Translated<typeof en>;

const ms: PortalDictionary = {
  common: {
    signOut: "Log keluar",
    cancel: "Batal",
    continue: "Teruskan",
    saveChanges: "Simpan perubahan",
    sendMagicLink: "Hantar pautan ajaib saya",
    sending: "Menghantar…",
    checkInbox: "Semak peti masuk anda",
    linkExpired: "Pautan itu telah tamat tempoh",
    sendNewLink: "Hantar pautan baharu",
    useDifferentEmail: "Guna e-mel lain",
    orUseCode: "Tidak dapat membuka pautan? Masukkan kod daripada e-mel yang sama.",
    codeLabel: "Kod daripada e-mel",
    signInWithCode: "Log masuk dengan kod",
    checkingCode: "Menyemak…",
    codeInvalid: "Kod itu tidak berjaya. Mungkin sudah tamat tempoh: hantar pautan baharu dan guna e-mel terbaharu.",
    codeFormat: "Masukkan kod 6 hingga 10 digit daripada e-mel.",
  },

  comfort: {
    title: "Keselesaan & paparan",
    calm: "Tenang",
    text: "Teks",
    spacing: "Jarak",
    contrast: "Kontras",
    dark: "Gelap",
    light: "Terang",
    calmMode: "Mod tenang",
    biggerText: "Teks lebih besar",
    readingSpacing: "Jarak bacaan",
    higherContrast: "Kontras lebih tinggi",
    darkTheme: "Tema gelap",
  },

  language: {
    title: "Bahasa",
    subtitle:
      "Bahasa Inggeris dan Bahasa Melayu kini — lebih banyak, termasuk bahasa kanan-ke-kiri, akan menyusul.",
  },

  service: {
    nav: {
      space: "Ruang anda",
      home: "Laman utama",
      children: "Kanak-kanak",
      assign: "Tetapkan latihan",
      export: "Eksport kemajuan",
      link: "Pautkan kanak-kanak",
      consent: "Perkongsian & keizinan",
      settings: "Tetapan",
    },
    signIn: {
      title: "Log masuk ke Zooylo",
      body: "Tiada kata laluan. Masukkan e-mel anda dan kami akan hantar pautan ajaib — satu ketik, anda masuk.",
      emailLabel: "E-mel anda",
      emailHint: "Kami hanya e-mel tentang Zooylo. Tiada spam, selamanya.",
      guardrail: "Teman membina kemahiran — bukan alat terapi atau diagnostik.",
      expiredBody:
        "Pautan ajaib sah 15 minit demi keselamatan akaun anda. Jangan risau — kami boleh hantar yang baharu sekarang.",
    },
    home: {
      greeting: "Selamat pagi",
      thisWeek: "Minggu ini",
      sessionsPlayed: "Sesi dimainkan",
      childrenActive: "Kanak-kanak aktif",
      summariesShared: "Ringkasan dikongsi",
      engagementOnly: "Penglibatan sahaja — bukan markah atau keputusan.",
      seeProgress: "Lihat kemajuan penuh",
      lastPlayed: "Kali terakhir bermain {date}",
      rosterTitle: "Minggu ini, setiap kanak-kanak",
      rosterDays: "Hari bermain: {n}",
      rosterSteps: "Langkah yang naik peringkat: {n}",
      rosterLongHelp: "Dengan sedikit bantuan selama 6+ minggu: {n}",
      rosterSeen: "Cadangan dilihat: {n}",
      rosterQuiet: "Belum ada permainan disegerakkan minggu ini",
      noStepsYet: "Belum ada langkah kemahiran dikongsi. Permainan terkini ada di halaman kanak-kanak.",
    },
    children: {
      title: "Kanak-kanak anda",
      ownerTag: "Anda pemilik",
      viewerTag: "Dikongsi oleh",
      pendingTag: "Menunggu persetujuan",
      revokedTag: "Akses ditarik balik",
      emptyTitle: "Belum ada kanak-kanak dipaut",
      emptyBody:
        "Pautkan kanak-kanak dengan kod, atau kongsi kod anda supaya dewasa lain boleh sertai. Tiada dikongsi tanpa keizinan.",
      loading: "Memuat kanak-kanak anda…",
    },
    childDetail: {
      yourRole: "Peranan anda: {role}",
      yourLabel: "Bagaimana anda dilihat oleh keluarga",
      labelHint: "Nama ringkas untuk anda, seperti Cik Aisyah, pertuturan. Tentang anda, bukan kanak-kanak.",
      saveLabel: "Simpan",
      labelSaved: "Disimpan.",
      labelError: "Kami tidak dapat menyimpannya. Sila cuba lagi.",
      editLabel: "Sunting",
      noLabel: "Belum ada label",
      adultsTitle: "Orang dewasa yang menyokong kanak-kanak ini",
      adultsNone: "Anda belum berkongsi dengan sesiapa.",
      adultActive: "Perkongsian aktif",
      adultPending: "Menunggu anda berkongsi",
      adultRevoked: "Perkongsian tamat",
      adultsError: "Kami tidak dapat memuatkan dengan siapa anda berkongsi.",
      roleFamily: "Keluarga",
      roleTherapist: "Ahli terapi",
      roleTeacher: "Guru",
      roleCaregiver: "Penjaga",
      roleOther: "Lain-lain",
      backToChildren: "Kembali ke kanak-kanak",
      tabsLabel: "Tentang kanak-kanak ini",
      tabOverview: "Gambaran keseluruhan",
      tabMessages: "Mesej",
      messagesTitle: "Cadangan latihan untuk kanak-kanak ini",
      messagesIntro: "Setiap cadangan yang dihantar untuk kanak-kanak ini, oleh anda dan oleh orang yang membantu, terbaharu dahulu.",
      messagesIntroHelper: "Cadangan yang anda hantar untuk kanak-kanak ini, terbaharu dahulu. Cadangan pembantu lain kekal antara mereka dan keluarga.",
      messagesNone: "Belum ada cadangan latihan dihantar untuk kanak-kanak ini.",
      messagesHidden: "Cadangan latihan dipaparkan di sini apabila keluarga berkongsi keputusan latihan ditetapkan dengan anda.",
      messagesLoading: "Memuat cadangan latihan…",
      messagesError: "Kami tidak dapat memuat cadangan latihan.",
      messagesRetry: "Cuba lagi",
      messagesSend: "Hantar cadangan latihan",
      senderYou: "Anda",
      senderOther: "Pembantu lain",
      sentOn: "Dihantar {date}",
      sentGames: "Permainan dalam mesej ini",
      recentPlay: "Permainan terkini",
      noSessions: "Tiada sesi permainan terkini",
      loading: "Memuat…",
      notLinked: "Kanak-kanak ini tidak dipautkan ke akaun anda.",
      noStepsYet:
        "Langkah kemahiran dipaparkan di sini setelah aplikasi keluarga berkongsi. Versi aplikasi yang lebih lama hanya berkongsi permainan terkini, seperti di bawah.",
      footer: "Apa yang kami lihat semasa bermain di rumah. Bukan penilaian, saringan atau diagnosis.",
      pendingTitle: "Menunggu persetujuan",
      pendingBody:
        "Pemilik belum mengesahkan perkongsian lagi. Anda akan melihat ringkasan penglibatan di sini setelah mereka berbuat demikian.",
      revokedTitle: "Akses ditarik balik",
      revokedBody:
        "Pemilik telah menarik balik akses anda kepada ringkasan penglibatan kanak-kanak ini.",
    },
    assign: {
      title: "Tetapkan latihan",
      subtitle:
        "Pilih pemandu dan permainan yang sepadan dengan kemahiran yang anda sasarkan dengan lembut. Kanak-kanak hanya buka aplikasi dan bermain.",
      step1: "Pilih kanak-kanak",
      step2: "Pilih sehingga 3 permainan",
      step3: "Tambah nota dan hantar",
      startButton: "Mula satu tugasan",
      chosenCount: "{n} daripada {max} dipilih",
      maxReached: "Itu 3, satu set pendek yang mudah diikuti keluarga di rumah. Nyahtanda satu untuk memilih permainan lain.",
      next: "Seterusnya",
      back: "Kembali",
      confirmFor: "Untuk",
      confirmGames: "Permainan",
      alreadySent: "Pernah dihantar: {games}. Menghantar semula menambah cadangan baharu.",
      confirmBody: "Keluarga melihat ini sebagai cadangan latihan dalam aplikasi mereka. Kanak-kanak hanya bermain; tiada apa-apa di sini ialah tugasan.",
      sendButton: "Hantar cadangan latihan",
      sending: "Menghantar…",
      sentTitle: "Cadangan latihan dihantar",
      sentBody: "Dihantar untuk {child}: {games}. Keluarga akan melihatnya apabila membuka aplikasi nanti.",
      sendMore: "Hantar cadangan lain",
      viewChild: "Lihat {child}",
      chooseOne: "Pilih sekurang-kurangnya satu permainan.",
      tooMany: "Pilih sehingga 3 permainan pada satu masa.",
      noteTooLong: "Nota boleh mengandungi sehingga 280 aksara.",
      noteLabel: "Nota untuk keluarga (pilihan)",
      noteHint: "Satu nota untuk semua permainan yang anda pilih. Keluarga melihatnya dalam aplikasi mereka.",
      notePrivacy: "Jangan masukkan nama atau butiran kesihatan.",
      noteCount: "Aksara: {n} daripada {max}",
      needResults:
        "Keluarga belum berkongsi \"Keputusan latihan ditetapkan\" dengan anda, jadi cadangan tidak dapat sampai ke aplikasi mereka. Mereka boleh menghidupkannya dalam Perkongsian & keizinan.",
      sendError: "Tidak dapat menghantar sekarang. Sila cuba lagi.",
      premiumTag: "Premium",
      premiumLocked: "Keluarga ini menggunakan versi percuma, jadi permainan Premium tidak boleh dicadangkan.",
    },
    export: {
      title: "Eksport kemajuan",
      subtitle:
        "Jana ringkasan tenang & bersih dakwaan — sedia untuk mesyuarat dan semakan. Tiada markah, jargon atau diagnosis.",
      bullet1: "Bahasa mudah, berpaksikan kekuatan",
      bullet2: "Penglibatan sahaja — tiada dakwaan diagnostik",
      bullet3: "Membawa baris perlindungan secara automatik",
      generateButton: "Jana PDF",
      shareButton: "Kongsi",
      preparing: "Menyediakan…",
      shareTitle: "Ringkasan sokongan Zooylo",
      shareText: "Apa yang kami lihat semasa bermain di rumah. Bukan penilaian, saringan atau diagnosis.",
    },
    link: {
      title: "Pautkan kanak-kanak",
      subtitle:
        "Kanak-kanak dicipta pada peranti sendiri dan kekal sebagai kod nama samaran. Pautkan dengan kod — sama ada yang dikongsi dengan anda, atau yang anda kongsi untuk menjemput dewasa lain.",
      haveTab: "Seseorang kongsi kod dengan saya",
      shareTab: "Saya menjaga kanak-kanak ini",
      codeLabel: "Kod pautan",
      linkButton: "Pautkan kanak-kanak ini",
    },
    consent: {
      title: "Perkongsian & keizinan",
      scopeProgress: "Ringkasan kemajuan",
      scopeResults: "Keputusan latihan ditetapkan",
      scopeExport: "Benarkan eksport kemajuan",
      revokeAll: "Tarik semua akses",
      immediateNote:
        "Menarik balik memutuskan akses serta-merta dan memadam salinan dikongsi yang dipegang dewasa itu. Anda kekal menyimpan data sendiri pada peranti ini.",
      whatShared: "Apa yang dikongsi",
      neverShared: "Apa yang tidak pernah dikongsi",
    },
    settings: {
      title: "Tetapan",
      subtitle: "Akaun, bahasa, mod keselesaan anda dan rasa Zooylo untuk anda.",
      accountTitle: "Akaun",
      signedInAs: "Dilog masuk sebagai",
      signInMethod: "Kaedah log masuk",
      magicLink: "Pautan ajaib · tiada kata laluan",
    },
    sync: {
      lastSynced: "Terakhir disegerakkan",
      note: "Ringkasan penglibatan yang diizinkan — mungkin lengah sedikit, kerana kanak-kanak bermain di luar talian.",
    },
    progress: {
      seenAtHomeTitle: "Dilihat di rumah",
      seenAtHomeNone: "Tiada nota dikongsi dari rumah dalam tempoh ini.",
      seenAtHomeWeek: "Minggu {date}",
      seenAtHomeNote: "Ditulis oleh keluarga dalam aplikasi dan dikongsi dengan anda.",
      onePagerTitle: "Ringkasan sokongan",
      sharedWithFamily: "Dikongsi dengan keluarga",
      period: "13 minggu lepas: {from} hingga {to}",
      supportTitle: "Cara terbaik menyokong saya",
      supportNone: "Belum ada tetapan dikongsi daripada aplikasi.",
      supportDefaults: "Menggunakan tetapan biasa aplikasi.",
      captions: "Sari kata dihidupkan",
      reducedMotion: "Kurangkan gerakan",
      highContrast: "Kontras tinggi",
      calmMode: "Mod tenang",
      tapOnly: "Ketik, bukan seret",
      language: "Bermain dalam {language}",
      langEn: "Bahasa Inggeris",
      langMs: "Bahasa Melayu",
      practiceTitle: "Latihan di rumah dalam tempoh ini",
      daysPlayed: "Bermain pada {n} hari",
      daysPlayedOne: "Bermain pada 1 hari",
      daysPlayedNone: "Tiada permainan disegerakkan dalam tempoh ini",
      activeWeeks: "Aktif dalam {n} daripada {total} minggu",
      stepsMovedUp: "Langkah yang naik peringkat: {n}",
      stepsInProgress: "Langkah yang sedang dicuba: {n}",
      newTitle: "Baharu sejak semakan lepas",
      newNone: "Tiada yang baharu dalam tempoh ini, dan itu tidak mengapa.",
      workingTitle: "Sedang dicuba sekarang",
      workingNone: "Tiada langkah di peringkat ini sekarang.",
      stepLine: "{skill}, langkah {n}",
      reachedOn: "Dicapai {date}",
      since: "Dengan sedikit bantuan sejak {date}",
      lastPractised: "Kali terakhir dimainkan {date}",
      notPractised: "Belum dimainkan",
      areasTitle: "Langkah tanpa bantuan permainan, awal dan akhir tempoh ini",
      areasNote: "Setiap bidang hanya dibandingkan dengan permulaan tempoh kanak-kanak ini sendiri.",
      areaAria: "{area}: {start} pada awal tempoh, {end} pada akhir",
      timelineTitle: "Garis masa",
      timelineHint:
        "Jalur pudar: langkah dimulakan. Jalur gelap: langkah tanpa bantuan permainan. Garis titik: semakan setiap 13 minggu.",
      summaryTitle: "Langkah tanpa bantuan permainan, mengikut peringkat",
      summaryAria: "Minggu {date}: {own} tanpa bantuan permainan, {steady} konsisten, {places} di pelbagai tempat",
      rowAria: "{skill}, minggu {date}: {started} daripada 4 langkah dimulakan, {own} tanpa bantuan permainan",
      rowCounts: "Dimulakan {started} daripada 4 · tanpa bantuan permainan {own}",
      rangeLabel: "Julat masa",
      range3m: "3 bulan",
      range6m: "6 bulan",
      range1y: "1 tahun",
      rangeAll: "Semua",
      reviewMarker: "Titik semakan, minggu {date}",
      weekOf: "Minggu {date}",
      stepStage: "Langkah {n}: {stage}",
      statusAssigned: "Belum dibuka",
      statusOpened: "Dibuka oleh keluarga",
      statusSeen: "Dilihat oleh keluarga pada {date}",
      statusPlayed: "Sudah dimainkan",
      stageNone: "Belum bermula",
      stageExploring: "Meneroka",
      stageHelp: "Dengan sedikit bantuan",
      stageOwn: "Tanpa bantuan permainan",
      stageSteady: "Konsisten",
      stagePlaces: "Di pelbagai tempat",
      bedLanguage: "Perkataan & mendengar",
      bedLiteracy: "Bunyi & huruf",
      bedNumeracy: "Nombor",
      bedExecutiveFunction: "Ingatan, corak & mengisih",
      bedSocialEmotional: "Perasaan & bergaul",
      bedDailyLife: "Kehidupan harian & keselamatan",
      bedMotor: "Tangan & menyurih",
      bedPerception: "Melihat & warna",
      bedCreativity: "Mencipta & berimaginasi",
      bedAttention: "Tumpuan",
      bedOther: "Lain-lain",
    },
  },

  console: {
    title: "Konsol Zooylo",
    subtitle: "Organisasi, terma lesen, invois dan kod akses.",
    eyebrow: "Konsol pemilik",
    notFoundTitle: "Halaman tidak ditemui",
    notFoundBody: "Tiada apa-apa di alamat ini.",
    backHome: "Pergi ke portal",
    tabOrgs: "Organisasi",
    tabCodes: "Kod akses",
    colName: "Organisasi",
    colKind: "Jenis",
    colStatus: "Status",
    colAdmins: "Pentadbir",
    colMembers: "Ahli",
    colLicences: "Lesen",
    colPrice: "Harga",
    colOutstanding: "Belum dibayar",
    kindOrg: "Organisasi",
    kindIndividual: "Pengamal individu",
    stActive: "Aktif",
    stSuspended: "Digantung",
    stClosed: "Ditutup",
    licencesOf: "{live} daripada {allowance}",
    noOrgs: "Belum ada organisasi.",
    manage: "Urus",
    closePanel: "Tutup",
    termsTitle: "Terma lesen",
    seats: "Tempat duduk staf",
    allowance: "Lesen keluarga",
    price: "Bahagian Zooylo (sebulan)",
    retailPrice: "Keluarga membayar organisasi (sebulan)",
    orgKeeps: "Organisasi simpan",
    orgKeepsHint: "Bayaran keluarga, tolak bahagian Zooylo. Dikira untuk anda.",
    sstRate: "Kadar SST (%)",
    errShareAboveRetail: "Bahagian Zooylo tidak boleh melebihi bayaran keluarga kepada organisasi.",
    errSstRange: "SST mesti antara 0 dan 100 peratus.",
    currency: "Mata wang",
    billingEmail: "E-mel bil",
    contractEnd: "Kontrak tamat",
    saveTerms: "Simpan terma",
    saved: "Disimpan.",
    statusTitle: "Status",
    suspend: "Gantung",
    reactivate: "Aktifkan semula",
    closeOrg: "Tutup organisasi",
    endLicencesLabel: "Tamatkan juga semua lesen keluarga sekarang",
    endLicencesWarn: "Setiap keluarga pada lesen organisasi ini kembali kepada haiwan percuma apabila aplikasi mereka dimuat semula. Lesen yang ditamatkan tidak boleh dipulihkan dari sini.",
    statusWarnSuspend: "Penggantungan menghentikan lesen keluarga baharu. Pentadbir masih boleh log masuk dan melihat skrin mereka.",
    statusWarnClose: "Penutupan menandakan organisasi sebagai ditutup. Pentadbir melihat notis ditutup.",
    statusWarnReactivate: "Pengaktifan semula membolehkan organisasi ini memberi lesen keluarga semula.",
    confirmStatus: "Sahkan",
    statusSaved: "Status dikemas kini.",
    invoiceTitle: "Keluarkan invois",
    periodStart: "Tempoh bermula",
    periodEnd: "Tempoh tamat",
    amountLabel: "Jumlah, pilihan",
    amountHint: "Biarkan kosong untuk mengecaj bulan-lesen darab bahagian Zooylo, dikira semasa invois dikeluarkan. Jumlah yang dimasukkan menggantikan subjumlah; SST sentiasa ditambah.",
    lastMonthEstimate: "Bulan lepas berjumlah {amount}.",
    periodEstimate: "Untuk tempoh ini: {lm} bulan-lesen. Subjumlah {subtotal}, SST ({rate}%) {sst}, jumlah {total}.",
    due: "Tarikh akhir",
    note: "Nota",
    issue: "Keluarkan invois",
    issued: "Invois dikeluarkan.",
    invoicesTitle: "Invois",
    noInvoices: "Tiada invois untuk organisasi ini.",
    markPaid: "Tanda dibayar",
    markVoid: "Batalkan",
    markIssued: "Tanda dikeluarkan",
    codesTitle: "Kod akses",
    newCode: "Cipta kod akses",
    codeLabel: "Label",
    codeKind: "Untuk",
    codeSeats: "Tempat duduk staf",
    codeAllowance: "Lesen keluarga",
    codePrice: "Bahagian Zooylo (sebulan)",
    codeRetailPrice: "Keluarga membayar organisasi (sebulan)",
    codeUses: "Penggunaan",
    codeExpires: "Tamat pada",
    create: "Cipta kod",
    creating: "Mencipta…",
    newCodeTitle: "Kod akses baharu",
    newCodeBody: "Berikan kod ini kepada organisasi. Ia dipaparkan sekali sahaja di sini, jadi salin sekarang.",
    copy: "Salin",
    copied: "Disalin",
    colCode: "Kod",
    colLabel: "Label",
    colTerms: "Terma",
    colUses: "Penggunaan",
    colExpires: "Tamat",
    termsLine: "{seats} tempat duduk · {families} lesen · {price}",
    never: "Tiada",
    noCodes: "Belum ada kod akses.",
    loading: "Memuatkan…",
    errorTitle: "Kami tidak dapat memuatkan konsol",
    retry: "Cuba lagi",
    errNotAuth: "Hanya pentadbir platform Zooylo boleh berbuat begitu.",
    errGeneric: "Itu tidak berjaya. Tiada apa yang berubah. Sila cuba lagi.",
    errInvalid: "Semak nilai dan cuba lagi.",
    demoNote: "Data demo. Tiada apa di sini sampai kepada organisasi sebenar.",
  },

  admin: {
    nav2: {
      children: "Kanak-kanak",
      licences: "Lesen keluarga",
      billing: "Bil",
      users: "Pengguna",
      settings: "Tetapan",
      consentData: "Keizinan & data",
      consent: "Keizinan & privasi",
      data: "Hak data",
      console: "Konsol Zooylo",
      demoAs: "Organisasi demo",
      demoCentre: "Pusat",
      demoIndividual: "Pengamal individu",
      demoSuspended: "Pusat digantung",
    },
    status: {
      suspendedTitle: "Organisasi anda dijeda",
      suspendedBody: "Zooylo telah menjeda akaun ini. Keluarga mengekalkan apa yang mereka ada dan anda masih boleh melihat setiap skrin, tetapi lesen keluarga baharu tidak boleh diberi. Tulis kepada support@zooylo.app untuk berbincang.",
      closedTitle: "Organisasi ini telah ditutup",
      closedBody: "Akaun ini telah ditutup. Anda masih boleh melihat rekodnya. Tulis kepada support@zooylo.app jika anda ada soalan mengenainya.",
    },
    licences: {
      title: "Lesen keluarga",
      subtitle: "Lesen keluarga memberi satu keluarga akses penuh kepada aplikasi, dibayar oleh organisasi anda. Anda perlukan alamat e-mel ibu bapa atau penjaga. Tiada maklumat kanak-kanak dipaparkan di sini.",
      chargeNote: "Lesen dicaj dari hari keluarga log masuk, mengikut bilangan hari.",
      allowance: "Dalam pelan anda",
      given: "Diberi",
      claimed: "Sudah log masuk",
      pending: "Menunggu log masuk",
      remaining: "Baki untuk diberi",
      noAllowanceTitle: "Pelan anda tiada lesen keluarga",
      noAllowanceBody: "Anda masih boleh memaut kepada kanak-kanak dan menghantar cadangan latihan. Keluarga yang mahukan akses penuh boleh melanggan dalam aplikasi, atau tanya kami tentang menambah lesen.",
      formTitle: "Beri lesen",
      emailLabel: "E-mel ibu bapa atau penjaga",
      endsLabel: "Lesen tamat pada",
      checkButton: "Semak e-mel ini",
      checking: "Menyemak…",
      giveButton: "Beri lesen",
      giving: "Memberi…",
      checkPaid: "Sudah ada Premium melalui gedung aplikasi. Lesen tidak diperlukan.",
      checkLicensed: "Sudah dilesenkan oleh anda.",
      checkAvailable: "Sedia untuk diberi. Baki anda {n}.",
      checkFull: "Tiada lesen berbaki. Tamatkan lesen yang tidak diperlukan lagi, atau minta kami menambah.",
      checkNoAllowance: "Pelan anda tiada lesen keluarga.",
      checkSuspended: "Organisasi anda digantung, jadi lesen tidak boleh diberi buat masa ini.",
      emailInvalid: "Masukkan alamat e-mel, seperti nama@example.com.",
      dateInvalid: "Pilih tarikh tamat pada masa hadapan.",
      givenOk: "Lesen diberi kepada {email}. Kami menghantar e-mel kepada keluarga tentang cara bermula: mereka log masuk ke aplikasi dengan alamat ini.",
      tableTitle: "Lesen",
      colFamily: "Keluarga",
      colStatus: "Status",
      colEnds: "Tamat",
      colGiven: "Diberi",
      colActions: "Tindakan",
      statusPending: "Menunggu log masuk",
      statusActive: "Sudah log masuk",
      statusLapsed: "Tamat",
      statusRevoked: "Ditamatkan awal",
      endingSoon: "Tamat dalam {n} hari",
      endingToday: "Tamat hari ini",
      changeEnd: "Tukar tarikh tamat",
      newEndLabel: "Tarikh tamat baharu untuk {email}",
      saveDate: "Simpan tarikh",
      endNow: "Tamatkan sekarang",
      endConfirmTitle: "Tamatkan lesen ini sekarang?",
      endConfirmBody: "Keluarga kembali kepada haiwan percuma apabila aplikasi mereka bersambung semula. Main anak mereka kekal pada peranti mereka.",
      endConfirmButton: "Tamatkan lesen",
      endedOk: "Lesen ditamatkan.",
      dateSaved: "Tarikh tamat disimpan.",
      empty: "Belum ada lesen diberi.",
      showPast: "Tunjuk lesen yang tamat ({n})",
      hidePast: "Sembunyi lesen yang tamat",
      demoPaidHint: "Demo: siti.rahman@example.com sudah membayar di gedung aplikasi.",
      loading: "Memuatkan lesen…",
      errorTitle: "Kami tidak dapat memuatkan lesen anda",
      retry: "Cuba lagi",
      errGeneric: "Itu tidak berjaya. Tiada apa yang berubah. Sila cuba lagi.",
      errNotAuth: "Hanya pentadbir organisasi ini boleh berbuat begitu.",
      errRefused: "Lesen itu tidak dapat diberi. Semak e-mel dan tarikh tamat, kemudian cuba lagi.",
      bulkTitle: "Beri banyak lesen sekaligus",
      bulkIntro: "Tampal e-mel ibu bapa atau penjaga, satu setiap baris atau dipisahkan dengan koma, atau pilih fail CSV. Setiap lesen dalam kumpulan ini tamat pada tarikh yang sama.",
      bulkPasteLabel: "E-mel ibu bapa atau penjaga",
      bulkFileLabel: "Atau pilih fail CSV",
      bulkFileHint: "Kami guna lajur bernama email, atau lajur pertama. Baris tajuk dilangkau.",
      bulkFileLoaded: "Dibaca daripada {file}.",
      bulkFileError: "Fail itu tidak dapat dibaca. Simpan sebagai fail CSV dan cuba lagi.",
      bulkEndsLabel: "Lesen tamat pada",
      bulkReady: "{n} e-mel sedia.",
      bulkInvalidNote: "{n} bukan e-mel. Ia akan disenaraikan sebagai bukan e-mel.",
      bulkDuplicateNote: "{n} e-mel berulang dikira sekali.",
      bulkLeftNote: "Anda ada {n} lesen lagi. E-mel selepas itu akan disenaraikan sebagai tiada lesen lagi.",
      bulkOverCap: "Itu lebih daripada 2000 e-mel. Bahagikan senarai dan muat naik secara berperingkat.",
      bulkGive: "Beri {n} lesen",
      bulkGiving: "Memberi lesen…",
      bulkResultsTitle: "Keputusan",
      bulkSummary: "Lesen diberi: {given}. Tidak diberi: {other}.",
      bulkColEmail: "E-mel",
      bulkColResult: "Keputusan",
      bulkStLicensed: "Berlesen",
      bulkStAlready: "Sudah berlesen",
      bulkStPaid: "Membayar di gedung aplikasi, tiada lesen diperlukan",
      bulkStInvalid: "Bukan e-mel",
      bulkStFull: "Tiada lesen lagi",
      bulkStSuspended: "Organisasi digantung",
      bulkNewList: "Mula senarai baharu",
      bulkDemoHint: "Demo: siti.rahman@example.com membayar di gedung aplikasi, dan priya.menon@example.com sudah berlesen.",
      selectRow: "Pilih {email}",
      selectAllShown: "Pilih semua yang dipaparkan",
      selectBeforeLabel: "Pilih semua yang tamat sebelum",
      selectBeforeButton: "Pilih",
      selectedCount: "{n} dipilih",
      clearSelection: "Kosongkan pilihan",
      bulkEndLabel: "Tarikh tamat baharu bagi lesen yang dipilih",
      bulkEndButton: "Tukar {n} tarikh tamat",
      bulkEndDone: "Tarikh tamat ditukar bagi {n} lesen.",
      bulkEndAllowance: "Itu akan membuka semula lebih banyak lesen daripada yang ada dalam pelan anda. Pilih lebih sedikit, atau tamatkan dahulu lesen yang tidak diperlukan.",
      fixEmail: "Betulkan e-mel",
      fixTitle: "Betulkan e-mel pada lesen ini",
      fixLabel: "E-mel yang betul",
      fixBodyPending: "Belum ada sesiapa log masuk dengan {email}, jadi lesen hanya dipindahkan ke e-mel yang betul.",
      fixBodyClaimed: "Seseorang sudah log masuk dengan {email}. Akses mereka tamat sekarang, dan e-mel yang betul mendapat lesen baharu dengan tarikh tamat yang sama.",
      fixButton: "Simpan e-mel",
      fixDone: "E-mel dibetulkan. Lesen kini untuk {email}.",
    },
    billing: {
      title: "Bil",
      subtitle: "Kos lesen keluarga anda, dan invois yang kami hantar kepada anda. Tempat duduk staf adalah percuma.",
      chargeNote: "Lesen dicaj dari hari keluarga log masuk, mengikut bilangan hari.",
      price: "Bahagian Zooylo setiap lesen keluarga",
      perMonth: "{amount} sebulan",
      plusSst: "Tambah SST {rate}%",
      noSst: "Tiada SST ditambah",
      arrangementTitle: "Cara lesen keluarga dibayar",
      arrangement: "Keluarga membayar anda {retail} sebulan; anda simpan {keep}; Zooylo menginvois anda {share} setiap keluarga sebulan, tambah SST {rate}%.",
      arrangementNoSst: "Keluarga membayar anda {retail} sebulan; anda simpan {keep}; Zooylo menginvois anda {share} setiap keluarga sebulan, tanpa SST.",
      arrangementShareOnly: "Zooylo menginvois anda {share} setiap keluarga sebulan, tambah SST {rate}%.",
      arrangementShareOnlyNoSst: "Zooylo menginvois anda {share} setiap keluarga sebulan, tanpa SST.",
      arrangementCollect: "Anda sendiri mengutip bayaran daripada keluarga. Zooylo menghantar satu invois sebulan kepada anda.",
      amountsIncludeSst: "Jumlah di bawah termasuk SST.",
      live: "Lesen aktif",
      claimedOf: "{claimed} sudah log masuk, {pending} menunggu",
      monthToDate: "Bulan ini setakat ini",
      lmLine: "{n} bulan-lesen",
      projected: "Bulan ini jika tiada perubahan",
      projectedHint: "Setiap lesen yang sudah log masuk untuk sebulan penuh",
      lastMonth: "Bulan lepas",
      outstanding: "Belum dibayar",
      outstandingNone: "Tiada yang belum dibayar",
      invoicesTitle: "Invois",
      colNumber: "Invois",
      colPeriod: "Tempoh",
      colLm: "Bulan-lesen",
      colAmount: "Jumlah",
      colSubtotal: "Subjumlah",
      colSst: "Amaun SST",
      colTotal: "Jumlah keseluruhan",
      colStatus: "Status",
      colDue: "Tarikh akhir",
      stIssued: "Dikeluarkan",
      stPaid: "Dibayar",
      stVoid: "Dibatalkan",
      stDraft: "Draf",
      noInvoices: "Belum ada invois.",
      contract: "Kontrak",
      contractRange: "{from} hingga {to}",
      contractFrom: "Sejak {from}",
      freePlanTitle: "Tiada apa untuk dibayar",
      freePlanBody: "Pelan anda tiada lesen keluarga, jadi tiada caj. Tempat duduk untuk pasukan anda sentiasa percuma.",
      payNote: "Bayar melalui pindahan bank dengan nombor invois sebagai rujukan. Pertanyaan: support@zooylo.app",
      loading: "Memuatkan bil…",
      errorTitle: "Kami tidak dapat memuatkan bil",
      retry: "Cuba lagi",
    },
    analytics: {
      title: "Sepintas lalu",
      subtitle: "Bilangan sahaja. Tidak pernah kemajuan kanak-kanak.",
      people: "Orang",
      membersLine: "{members} ahli, {admins} pentadbir",
      justYou: "Anda seorang",
      children: "Kanak-kanak",
      linked: "Dipaut",
      active30: "Bermain dalam 30 hari lepas",
      inactive: "Dipindah ke tidak aktif",
      licences: "Lesen keluarga",
      live: "Aktif",
      claimed: "Sudah log masuk",
      endingSoon: "Tamat dalam 30 hari",
      suggestions: "Cadangan latihan, 30 hari lepas",
      sent: "Dihantar",
      seen: "Dilihat oleh keluarga",
      played: "Dimainkan",
      chartTitle: "Bulan-lesen mengikut bulan",
      chartAria: "{month}: {n} bulan-lesen",
      chartNote: "Bulan semasa menunjukkan hari setakat ini.",
      loading: "Memuatkan bilangan…",
      errorTitle: "Kami tidak dapat memuatkan bilangan ini",
      retry: "Cuba lagi",
    },
    kids: {
      title: "Kanak-kanak",
      subtitle: "Kanak-kanak yang disokong pasukan anda, dipaparkan sebagai kod, dengan sama ada mereka bermain. Tidak pernah kemajuan mereka.",
      refNote: "Setiap kanak-kanak dipaparkan sebagai rujukan berkod, bukan nama. Kod ini unik kepada organisasi anda dan tidak boleh dijejak semula. Identiti kanak-kanak kekal dengan keluarga mereka.",
      tabActive: "Aktif ({n})",
      tabInactive: "Tidak aktif ({n})",
      colChild: "Kanak-kanak",
      colSupported: "Disokong oleh",
      colSharing: "Perkongsian",
      colActivity: "Aktiviti",
      colLicence: "Lesen",
      colActions: "Tindakan",
      childCode: "Kanak-kanak · {ref}",
      actActive: "Aktif",
      actInactive: "Tidak aktif",
      actUnknown: "Belum disegerak",
      actNote: "Aktif bermaksud aplikasi keluarga menyegerak main dalam 30 hari lepas.",
      licLicensed: "Dilesenkan oleh anda",
      licPaid: "Bayar dalam aplikasi",
      licFree: "Percuma",
      shareActive: "Perkongsian aktif",
      sharePending: "Menunggu keluarga",
      shareRevoked: "Perkongsian tamat",
      moveInactive: "Pindah ke tidak aktif",
      moveActive: "Pindah semula ke aktif",
      endSupport: "Tamatkan sokongan",
      endTitle: "Tamatkan sokongan untuk kanak-kanak ini?",
      endBody: "Setiap pautan pasukan anda kepada kanak-kanak ini dibuang, bersama cadangan yang anda hantar. Keluarga mengekalkan semuanya dalam aplikasi mereka dan boleh memaut semula dengan kod baharu.",
      endButton: "Tamatkan sokongan",
      endedOk: "Sokongan ditamatkan. Pautan dibuang: {n}.",
      movedInactive: "Dipindah ke tidak aktif.",
      movedActive: "Dipindah semula ke aktif.",
      inactiveNote: "Memindah kanak-kanak ke tidak aktif tidak mengubah apa-apa untuk keluarga dan tidak membuang pautan.",
      emptyActive: "Belum ada kanak-kanak dipaut",
      emptyActiveBody: "Kanak-kanak muncul di sini setelah keluarga berkongsi dengan seseorang dalam pasukan anda.",
      emptyInactive: "Tiada kanak-kanak tidak aktif",
      emptyInactiveBody: "Pindahkan kanak-kanak ke sini apabila mereka pergi, supaya senarai aktif kekal terkini. Tiada apa berubah untuk keluarga.",
      loading: "Memuatkan kanak-kanak…",
      errorTitle: "Kami tidak dapat memuatkan senarai kanak-kanak",
      retry: "Cuba lagi",
      errGeneric: "Itu tidak berjaya. Tiada apa yang berubah. Sila cuba lagi.",
      roleFamily: "Keluarga",
      roleTherapist: "Ahli terapi",
      roleTeacher: "Guru",
      roleCaregiver: "Penjaga",
      roleOther: "Lain-lain",
      colConsent: "Keluarga",
      consentShared: "Keluarga berkongsi",
      consentNot: "Belum dikongsi",
      notSharedNote: "Keluarga boleh berkongsi kanak-kanak ini dengan organisasi anda daripada aplikasi selepas log masuk.",
      assignStaff: "Tetapkan kakitangan ({n})",
      closeStaff: "Tutup",
      staffTitle: "Kakitangan untuk {child}",
      staffIntro: "Kakitangan yang ditetapkan melihat kanak-kanak ini dalam portal penjaga mereka selagi keluarga terus berkongsi dengan organisasi anda.",
      staffNone: "Belum ada kakitangan ditetapkan.",
      staffRemove: "Buang",
      staffRemoveLabel: "Buang {email}",
      staffRemoved: "Dibuang. Mereka tidak lagi melihat kanak-kanak ini.",
      staffAdd: "Tambah kakitangan",
      staffMember: "Kakitangan",
      staffChoose: "Pilih seseorang",
      staffRole: "Peranan",
      staffAssign: "Tetapkan",
      staffAssigned: "{email} telah ditetapkan.",
      staffNoMembers: "Semua orang dalam organisasi anda sudah ditetapkan kepada kanak-kanak ini.",
      staffLoading: "Memuatkan kakitangan…",
      staffErrNotShared: "Keluarga belum berkongsi kanak-kanak ini dengan organisasi anda.",
      staffErrDirect: "Orang ini sudah mempunyai pautan sendiri kepada kanak-kanak ini daripada keluarga.",
      staffErrNotMember: "Hanya ahli organisasi anda boleh ditetapkan.",
    },
    users: {
      title: "Pengguna",
      seatsFree: "Tempat duduk untuk pasukan anda adalah percuma.",
      justYouTitle: "Anda seorang buat masa ini",
      justYouBody: "Anda mengendalikan akaun ini sendiri. Jika rakan sekerja menyertai anda kemudian, jemput mereka di sini.",
      inviteColleague: "Jemput rakan sekerja",
      soloSubtitle: "Akaun anda. Jemput rakan sekerja kemudian jika anda mula bekerja bersama.",
    },
    nav: {
      manage: "Urus",
      overview: "Gambaran",
      members: "Ahli",
      seats: "Kerusi & lesen",
      sponsored: "Keluarga ditaja",
      oversight: "Penyeliaan",
      roster: "Penyeliaan daftar",
      consent: "Keizinan & privasi",
      data: "Hak data",
      settings: "Tetapan organisasi",
    },
    signIn: {
      title: "Log masuk untuk urus organisasi anda",
      body: "Tiada kata laluan. Masukkan e-mel kerja anda dan kami akan hantar pautan ajaib untuk log masuk.",
      emailLabel: "E-mel kerja",
      emailHint: "Hanya orang yang dijemput ke organisasi anda boleh log masuk di sini.",
      guardrail: "Kanak-kanak kekal nama samaran — pentadbir tidak pernah lihat kemajuan kanak-kanak.",
      notMemberTitle: "E-mel ini belum sebahagian daripada organisasi",
    },
    overview: {
      title: "Gambaran organisasi",
      subtitle: "Gambaran kesihatan lesen dan aktiviti terkini anda.",
      licenceHealth: "Kesihatan lesen",
      planActive: "Pelan aktif",
      seatsFreeLabel: "Kerusi percuma",
      needMoreSeats: "Perlukan lebih kerusi?",
      membersWord: "Ahli",
      linkedChildrenLabel: "Kanak-kanak dipaut",
      pseudonymousNote: "Agregat nama samaran — pentadbir tidak pernah lihat kemajuan kanak-kanak",
      pendingInviteLabel: "Jemputan tertunda",
      pendingWhoLabel: "Menunggu penerimaan",
      recentActivity: "Aktiviti terkini",
    },
    members: {
      title: "Ahli",
      subtitle:
        "Jemput orang yang menyokong kanak-kanak — melalui e-mel. Mereka log masuk dengan pautan ajaib; tiada kata laluan.",
      inviteButton: "Jemput ahli",
      colMember: "Ahli",
      colRole: "Peranan",
      colSeat: "Kerusi",
      colStatus: "Status",
    },
    seats: {
      title: "Kerusi & lesen",
      subtitle:
        "Berikan, tetapkan semula atau tarik kerusi untuk mana-mana ahli. Pelan anda dirunding dengan kami — tiada pembayaran di sini.",
      capacity: "Kapasiti kerusi",
      contactUs: "Hubungi kami",
    },
    sponsored: {
      title: "Keluarga ditaja",
      subtitle:
        "Beri keluarga yang anda sokong akses penuh kepada aplikasi di bawah lesen organisasi anda, sehingga tarikh pilihan anda. Anda perlukan alamat e-mel ibu bapa atau penjaga — tiada maklumat kanak-kanak dipaparkan di sini.",
      allowanceLabel: "Penajaan",
      contactUs: "Hubungi kami",
    },
    roster: {
      title: "Penyeliaan daftar",
      subtitle:
        "Setiap kanak-kanak dipaut dalam organisasi anda — siapa menyokong mereka dan status perkongsian. Agregat sahaja; pentadbir tidak pernah lihat kemajuan.",
      emptyTitle: "Belum ada kanak-kanak dipaut",
    },
    consent: {
      title: "Penyeliaan keizinan & privasi",
      subtitle:
        "Log jelas tentang siapa boleh akses kanak-kanak mana, dan keizinan di sebaliknya. Keizinan diberikan dan ditarik balik oleh ibu bapa atau penjaga kanak-kanak — organisasi anda boleh mengauditnya, bukan mengubahnya.",
      banner:
        "Setiap kanak-kanak ialah UUID nama samaran tanpa data peribadi disimpan pada pelayan kami. Akses sentiasa berbalik kepada keizinan ibu bapa yang jelas dan boleh ditarik. Data disimpan di bawah undang-undang perlindungan data wilayah anda.",
    },
    data: {
      title: "Hak subjek data",
      subtitle:
        "Eksport apa yang organisasi anda simpan, atau buang. Kanak-kanak kekal nama samaran — tiada data peribadi kanak-kanak untuk dieksport.",
      exportButton: "Eksport data organisasi",
      closeTitle: "Tutup akaun organisasi",
    },
    settings: {
      title: "Tetapan organisasi",
      subtitle: "Profil organisasi, bahasa anda dan rasa portal untuk anda.",
      residencyTitle: "Wilayah pemastautinan data",
      residencyBody:
        "Tetapkan tempat rekod organisasi anda disimpan, mengikut undang-undang perlindungan data tempatan anda. Tiada wilayah diandaikan secara lalai.",
    },
  },
};

/** All locale dictionaries, keyed by locale — the runtime switcher's source. */
export const dictionaries: Record<Locale, PortalDictionary> = {
  en,
  ms,
};

/** Fill `{name}` placeholders in a dictionary template. */
export function fill(template: string, vars: Record<string, string | number>): string {
  return template.replace(/\{(\w+)\}/g, (m, k: string) => (k in vars ? String(vars[k]) : m));
}

/** Resolve a dictionary for a locale, falling back to the default. */
export function getDictionary(locale: Locale): PortalDictionary {
  return dictionaries[locale] ?? dictionaries[DEFAULT_LOCALE];
}
